/**
 * TierGate — declarative tier-feature gating component (C3B / NocoDB #4085).
 *
 * Wraps children that must only render for tiers whose entitlement manifest
 * declares `feature: true`. Replaces ad-hoc `{!isAnonymous && (...)}` and
 * `{tier === 'dramax' && (...)}` patterns at call sites.
 *
 * The manifest at `@dramadetector/shared/entitlements/manifest.ts` is the
 * source of truth. `useEntitlements().hasFeature()` reads from it.
 *
 *   <TierGate feature="feature:subscription_management">
 *     <SubscriptionSection ... />
 *   </TierGate>
 *
 * For PostHog-flagged features (`{ posthog_flag: 'beta_x' }` in the manifest),
 * `hasFeature()` returns `false` — wire those through PostHog's hook instead
 * once C3B server middleware lands. This component covers the static-tier
 * majority case (Subscription, Account, Retention sections etc.).
 */
import type { ReactNode } from 'react';
import { useEntitlements } from '../../hooks/useEntitlements';
import type { Feature } from '@dramadetector/shared/entitlements';

interface TierGateProps {
  feature: Feature;
  children: ReactNode;
  fallback?: ReactNode;
}

export function TierGate({ feature, children, fallback = null }: TierGateProps) {
  const { hasFeature } = useEntitlements();
  return hasFeature(feature) ? <>{children}</> : <>{fallback}</>;
}
